import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Search on the server; retain previously fetched labels for selected records.
export default function CultivarPicker({ speciesId, selected, onChange, lang }) {
  const [extended, setExtended] = useState(false);
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const cache = useRef(new Map());
  const it = lang === "it";
  const hasCuratedSelection = ["olea-europaea", "vitis-vinifera"].includes(speciesId);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      api.get("/botanical/cultivars", {
        params: { species_id: speciesId, q: query, offset, limit: 51, scope: !hasCuratedSelection || extended || query.trim() ? "all" : "essential", view: "picker" },
        signal: controller.signal,
      }).then(({ data }) => {
        if (!active) return;
        const page = data.slice(0, 50);
        page.forEach((record) => cache.current.set(record.id, record));
        setResults((previous) => offset ? [...previous, ...page] : page);
        setMore(data.length > 50);
      }).catch(() => {
        if (active) { setError(true); setMore(false); }
      }).finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [speciesId, query, offset, retry, extended, hasCuratedSelection]);

  useEffect(() => {
    const missing = selected.filter(id => !cache.current.has(id));
    if (!missing.length) return;
    let active = true;
    api.get("/botanical/cultivars", { params: { species_id: speciesId, ids: missing.join(","), limit: 500 } }).then(({ data }) => {
      if (active) { data.forEach(record => cache.current.set(record.id, record)); setResults(previous => [...previous]); }
    }).catch(() => {});
    return () => { active = false; };
  }, [speciesId, selected]);
  const rows = new Map(selected.map((id) => [id, cache.current.get(id) || { id, name: id }]));
  results.forEach((record) => rows.set(record.id, record));
  const resultIds = new Set(results.map((record) => record.id));
  return <details className="rounded-lg border border-[#E2DACB] bg-white p-3" data-testid="cultivar-picker">
    <summary className="cursor-pointer text-[#4A443D] font-medium">{it ? "Varietà" : "Varieties"} · {selected.length} {it ? "selezionate" : "selected"}</summary>
    <div className="space-y-2 mt-3">
    <input aria-label={it ? "Cerca varietà" : "Search varieties"}
      placeholder={it ? "Cerca nome o sinonimo…" : "Search name or synonym…"}
      value={query} maxLength={100} onChange={(event) => {
        setQuery(event.target.value); setOffset(0); setResults([]); setMore(false);
      }} className="w-full border rounded px-3 py-2" />
    <p className="text-xs text-[#6E6258]">{!hasCuratedSelection ? (it ? "Archivio documentato: cerca per nome. Fonti: SIAN 2025, Registro fruttiferi settembre 2026 e collezioni UCR/USDA. La presenza non certifica l’iscrizione attuale o la disponibilità commerciale." : "Documented archive: search by name. Sources: SIAN 2025, September 2026 fruit registry and UCR/USDA collections. Inclusion does not certify current registration or commercial availability.") : extended ? (it ? "Archivio esteso: alcune denominazioni simili identificano riferimenti distinti." : "Extended archive: similar names may identify distinct references.") : (it ? "Selezione essenziale. La ricerca per nome o sinonimo consulta tutto l’archivio." : "Essential selection. Searching a name or synonym searches the entire archive.")}</p>
    {hasCuratedSelection && <button type="button" className="text-sm underline text-[#557448]" onClick={() => { setExtended(v => !v); setOffset(0); setResults([]); setMore(false); }}>{extended ? (it ? "Torna alla selezione essenziale" : "Return to essential selection") : (it ? "Cerca nell’archivio esteso" : "Search extended archive")}</button>}
    {loading && <p role="status" className="text-sm">{it ? "Ricerca in corso…" : "Searching…"}</p>}
    {error && <div role="alert" className="text-sm">
      {it ? "Impossibile caricare le varietà." : "Unable to load varieties."}
      <button type="button" onClick={() => setRetry((n) => n + 1)} className="ml-2 underline">{it ? "Riprova" : "Retry"}</button>
    </div>}
    {!loading && !error && !results.length && <p role="status" className="text-sm">{it ? "Nessuna varietà trovata. Prova un altro nome." : "No varieties found. Try another name."}</p>}
    {[...rows.values()].map((record) => <div key={record.id}>
      <label className="flex items-center gap-2 text-sm text-[#4A443D]">
        <input type="checkbox" data-testid={`ob-cultivar-${record.id}`} checked={selected.includes(record.id)}
          onChange={(event) => onChange(record.id, event.target.checked)} />
        <span className="min-w-0 break-words">{record.display_name || record.name}{extended && record.source && <span className="text-xs text-[#6E6258]"> ({record.source}{record.source_id ? ` #${record.source_id}` : ""})</span>}
          {!!record.matched_synonyms?.length && resultIds.has(record.id) && <span className="block text-xs text-[#6E6258]">{it ? "Nome associato nella fonte" : "Associated source name"}: {record.matched_synonyms.slice(0, 3).join(", ")}</span>}
        </span>
      </label>
      {(!!record.synonyms?.length || record.source_url) && <details className="ml-6 text-xs text-[#6E6258]">
        <summary className="cursor-pointer">{it ? "Nomi associati nella fonte" : "Associated source names"} ({record.synonyms?.length || 0})</summary>
        <p>{it ? "Un nome associato può essere ambiguo: non implica che cultivar diverse siano identiche." : "An associated name may be ambiguous: it does not imply that distinct cultivars are identical."}</p>
        <p className="mt-1 break-words">{record.name}{record.synonyms?.length ? ` — ${record.synonyms.join(", ")}` : ""}</p>
        {(record.synonym_source_url || record.source_url) && <a href={record.synonym_source_url || record.source_url} target="_blank" rel="noopener noreferrer" className="underline">{it ? "Consulta la fonte" : "View source"}</a>}
      </details>}
    </div>)}
    {more && !error && <button type="button" disabled={loading} onClick={() => setOffset((n) => n + 50)} className="text-sm underline disabled:opacity-50">{it ? "Carica altre varietà" : "Load more varieties"}</button>}
  </div></details>;
}


